import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {CompanyService} from '@services/company.service';
import {IBodaccAlert, IWatchedCompany} from '@models/workspace.interface';
import {FileText, LucideAngularModule, Plus, RefreshCw, Trash2} from '@shared/components/icons/google-icons';
import {NotificationService} from '@services/frontend/notification.service';

@Component({
  selector: 'app-company-surveillance',
  imports: [DatePipe, FormsModule, LucideAngularModule],
  templateUrl: './company-surveillance.html',
})
export class CompanySurveillance implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyService = inject(CompanyService);
  private readonly notificationService = inject(NotificationService);

  watched = signal<IWatchedCompany[]>([]);
  allAlerts = signal<IBodaccAlert[]>([]);
  newSiren = '';
  newName = '';
  syncing = signal(false);
  private siret = '';

  filteredAlerts = computed(() => {
    const w = new Set(this.watched().map(x => x.watchedSiren.replace(/\D/g, '')));
    return this.allAlerts().filter(a => w.has(a.siren.replace(/\D/g, '')));
  });

  protected readonly Plus = Plus;
  protected readonly Trash2 = Trash2;
  protected readonly RefreshCw = RefreshCw;
  protected readonly FileText = FileText;

  ngOnInit() {
    this.siret = this.route.parent!.snapshot.paramMap.get('siret')!;
    this.refreshWatched();
    this.refreshAlerts();
  }

  refreshWatched() {
    this.companyService.listWatchedCompanies(this.siret).subscribe({
      next: w => this.watched.set(w),
      error: () => this.notificationService.error('Impossible de charger la liste de surveillance', 3000),
    });
  }

  refreshAlerts() {
    this.companyService.getAllBodaccAlerts().subscribe({
      next: a => this.allAlerts.set(a),
      error: () => {},
    });
  }

  sync() {
    this.syncing.set(true);
    this.companyService.syncBodacc().subscribe({
      next: () => {
        this.syncing.set(false);
        this.notificationService.success('Synchronisation terminée', 2000);
        this.refreshAlerts();
      },
      error: () => {
        this.syncing.set(false);
        this.notificationService.error('Synchronisation impossible', 3000);
      },
    });
  }

  add() {
    const s = this.newSiren.replace(/\D/g, '');
    if (s.length !== 9) {
      this.notificationService.warning('SIREN à 9 chiffres requis', 2500);
      return;
    }
    this.companyService.addWatchedCompany(this.siret, s, this.newName || null).subscribe({
      next: () => {
        this.newSiren = '';
        this.newName = '';
        this.refreshWatched();
        this.notificationService.success('Société ajoutée', 2000);
      },
      error: () => this.notificationService.error('Ajout impossible', 3000),
    });
  }

  remove(w: IWatchedCompany) {
    this.companyService.deleteWatchedCompany(this.siret, w.id).subscribe({
      next: () => {
        this.refreshWatched();
        this.notificationService.success('Retiré de la surveillance', 2000);
      },
      error: () => this.notificationService.error('Suppression impossible', 3000),
    });
  }
}
